import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState, useCallback } from 'react'
import { useMutation as useConvexMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { deckImportSchema, convertSongToFlashcards, type DeckImport } from '@/lib/flashcardSchemas'

export const Route = createFileRoute('/app/decks/add')({
  component: AddDeckPage,
})

function AddDeckPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to create decks</div>
      </div>
    )
  }

  return <AddDeckContent userId={currentUser._id} />
}

function AddDeckContent({ userId }: { userId: Id<'users'> }) {
  const navigate = useNavigate()
  const [deckName, setDeckName] = useState('')
  const [deckDescription, setDeckDescription] = useState('')
  const [importData, setImportData] = useState<DeckImport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isImporting, setIsImporting] = useState(false)

  const createDeck = useConvexMutation(api.flashcards.createDeck)
  const importFlashcards = useConvexMutation(api.flashcards.importFlashcards)

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setError(null)

    try {
      const text = await file.text()
      const parsedJson = JSON.parse(text)

      // Try to parse as deck import schema
      const parsed = deckImportSchema.parse(parsedJson)
      setImportData(parsed)
    } catch (err) {
      // Try to parse as song structure
      try {
        const text = await file.text()
        const parsedJson = JSON.parse(text)
        const songs = Array.isArray(parsedJson) ? parsedJson : [parsedJson]
        const flashcards = songs.flatMap((song: {
          title: string
          sections: Array<{ type: string; spanish: string; english: string }>
          targetVerb?: string
        }) => convertSongToFlashcards(song))
        setImportData({
          name: deckName || file.name.replace('.json', ''),
          description: deckDescription,
          flashcards,
        })
      } catch (parseError) {
        setError(
          err instanceof Error ? err.message : 'Invalid JSON format. Expected deck import or song structure.'
        )
        setImportData(null)
      }
    }
  }, [deckName, deckDescription])

  const handleCreateDeck = useCallback(async () => {
    if (!deckName.trim()) {
      setError('Deck name is required')
      return
    }

    setIsImporting(true)
    setError(null)

    try {
      // Create deck
      const deckId = await createDeck({
        userId,
        name: deckName.trim(),
        description: deckDescription.trim() || undefined,
      })

      // Import flashcards if provided
      if (importData && importData.flashcards.length > 0) {
        await importFlashcards({
          userId,
          deckId,
          flashcards: importData.flashcards.map((card) => ({
            frontSpanish: card.frontSpanish,
            backEnglish: card.backEnglish,
            cardType: card.cardType || 'lyrics',
            targetVerb: card.targetVerb,
            sourceVerb: card.sourceVerb,
            priority: card.priority,
            tags: card.tags,
            notes: card.notes,
          })),
        })
      }

      // Navigate to decks list
      navigate({ to: '/app/decks' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create deck')
      setIsImporting(false)
    }
  }, [deckName, deckDescription, importData, userId, createDeck, importFlashcards, navigate])

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 terminal-border p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [CREATE DECK]
          </h1>
          <p className="text-trail-tan font-mono text-sm sm:text-base">
            Create a new flashcard deck or import from JSON
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-900 border-2 border-red-500 rounded-lg p-4">
            <p className="text-red-200 font-mono text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Deck Info Form */}
          <div className="terminal-border p-4 sm:p-6 bg-black">
            <h2 className="text-xl font-bold text-terminal-green mb-4 font-mono">
              [DECK INFORMATION]
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                  ► DECK NAME
                </label>
                <input
                  type="text"
                  value={deckName}
                  onChange={(e) => setDeckName(e.target.value)}
                  placeholder="Enter deck name..."
                  className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                  ► DESCRIPTION <span className="text-trail-tan font-normal">(optional)</span>
                </label>
                <textarea
                  value={deckDescription}
                  onChange={(e) => setDeckDescription(e.target.value)}
                  placeholder="Enter deck description..."
                  className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* JSON Import */}
          <div className="terminal-border p-4 sm:p-6 bg-black">
            <h2 className="text-xl font-bold text-terminal-green mb-4 font-mono">
              [IMPORT FROM JSON]
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                  ► SELECT JSON FILE
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none file:mr-4 file:py-2 file:px-4 file:bg-terminal-green file:text-black file:border-0 file:font-bold file:font-mono file:cursor-pointer hover:file:bg-terminal-green/80"
                />
                <p className="text-trail-tan text-xs font-mono mt-2">
                  Supports deck import format or song structure from consts folder
                </p>
              </div>

              {/* Preview */}
              {importData && (
                <div className="bg-black border-2 border-terminal-green p-4">
                  <div className="text-terminal-green font-mono font-bold text-sm mb-2">
                    PREVIEW: {importData.flashcards.length} flashcard{importData.flashcards.length !== 1 ? 's' : ''} ready to import
                  </div>
                  <div className="text-trail-tan text-xs font-mono max-h-48 overflow-y-auto">
                    {importData.flashcards.slice(0, 5).map((card, idx) => (
                      <div key={idx} className="mb-2">
                        <div className="text-sky-blue">ES: {card.frontSpanish.join(' | ')}</div>
                        <div className="text-gray-400">EN: {card.backEnglish.join(' | ')}</div>
                      </div>
                    ))}
                    {importData.flashcards.length > 5 && (
                      <div className="text-gray-500">... and {importData.flashcards.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate({ to: '/app/decks' })}
              className="flex-1 px-6 py-3 bg-black text-trail-tan font-bold font-mono border-4 border-trail-tan hover:bg-trail-tan hover:text-black transition-all"
            >
              CANCEL
            </button>
            <button
              onClick={handleCreateDeck}
              disabled={isImporting || !deckName.trim()}
              className="flex-1 px-6 py-3 bg-terminal-green text-black font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow"
            >
              {isImporting ? 'CREATING...' : 'CREATE DECK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

